/**
 * Custom date picker dropdown inspired by Todoist.
 * Quick shortcuts + mini calendar + time pill + repeat options.
 */
import { useState, useMemo, useCallback } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { formatDate, useT } from "@/i18n";
import { panelsTaskScheduleMessages } from "@/i18n/messages/panels-task-schedule";
import {
  SHORTCUTS,
  fmtDate,
  fmtDateTime,
  getMonthGrid,
  isPast,
  isSameDay,
  type Shortcut,
} from "./date-picker-dates";
import {
  CLEAR_BUTTON_CLASS,
  DatePickerRepeatView,
  DatePickerSurface,
  DatePickerTimeView,
  MENU_ROW_CLASS,
} from "./date-picker-subviews";
import {
  buildRepeatOptions,
  repeatLabel,
  weekdayName,
  type RepeatOption,
} from "./repeat-options";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

interface DatePickerDropdownProps {
  value: string; // ISO "YYYY-MM-DDTHH:mm" or ""
  onChange: (val: string) => void;
  onClear: () => void;
  onRepeatChange?: (repeat: RepeatOption | null) => void;
  repeat?: RepeatOption | null;
}

type View = "main" | "time" | "repeat";

/* ---------- Component ---------- */

export function DatePickerDropdown({
  value,
  onChange,
  onClear,
  onRepeatChange,
  repeat,
}: DatePickerDropdownProps) {
  const t = useT(panelsTaskScheduleMessages);
  const today = useMemo(() => new Date(), []);
  const selectedDate = value ? new Date(value) : null;
  const existingTime = value?.includes("T") ? value.split("T")[1] : "";

  const [viewYear, setViewYear] = useState(
    selectedDate ? selectedDate.getFullYear() : today.getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate ? selectedDate.getMonth() : today.getMonth(),
  );
  const [timeValue, setTimeValue] = useState(existingTime || "");
  const [view, setView] = useState<View>("main");

  const weeks = useMemo(
    () => getMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const goToPrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const goToToday = useCallback(() => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  }, [today]);

  const selectDate = useCallback(
    (d: Date) => {
      const time = timeValue || "23:59";
      onChange(fmtDateTime(d, time));
    },
    [onChange, timeValue],
  );

  const handleShortcut = useCallback(
    (shortcut: Shortcut) => {
      const d = shortcut.getDate(today);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      selectDate(d);
    },
    [today, selectDate],
  );

  const handleTimePreset = useCallback(
    (time: string) => {
      setTimeValue(time);
      if (selectedDate) onChange(fmtDateTime(selectedDate, time));
      setView("main");
    },
    [selectedDate, onChange],
  );

  const handleCustomTime = useCallback(
    (time: string) => {
      setTimeValue(time);
      if (selectedDate) onChange(fmtDateTime(selectedDate, time));
    },
    [selectedDate, onChange],
  );

  const handleClearTime = useCallback(() => {
    setTimeValue("");
    if (selectedDate) onChange(fmtDateTime(selectedDate, "23:59"));
    setView("main");
  }, [selectedDate, onChange]);

  const handleRepeatSelect = useCallback(
    (opt: RepeatOption) => {
      onRepeatChange?.(opt);
      setView("main");
    },
    [onRepeatChange],
  );

  const handleClearRepeat = useCallback(() => {
    onRepeatChange?.(null);
    setView("main");
  }, [onRepeatChange]);

  const monthLabel = formatDate(new Date(viewYear, viewMonth, 1), {
    month: "short",
    year: "numeric",
  });
  const isCurrentMonth =
    viewYear === today.getFullYear() && viewMonth === today.getMonth();
  const repeatOptions = useMemo(
    () => buildRepeatOptions(selectedDate ?? today),
    [selectedDate, today],
  );

  if (view === "time") {
    return (
      <DatePickerTimeView
        timeValue={timeValue}
        onBack={() => setView("main")}
        onPreset={handleTimePreset}
        onCustomTime={handleCustomTime}
        onClear={handleClearTime}
      />
    );
  }

  if (view === "repeat") {
    return (
      <DatePickerRepeatView
        options={repeatOptions}
        repeat={repeat}
        onBack={() => setView("main")}
        onSelect={handleRepeatSelect}
        onClear={handleClearRepeat}
      />
    );
  }

  /* --- Main view --- */
  return (
    <DatePickerSurface>
      {/* Quick shortcuts */}
      <div className="space-y-0.5">
        {SHORTCUTS.map((sc) => (
          <button
            key={sc.id}
            type="button"
            onClick={() => handleShortcut(sc)}
            className={cn(MENU_ROW_CLASS, "gap-2.5")}
            data-testid={`date-shortcut-${sc.id}`}
          >
            {renderIcon(sc.icon)}
            <span className="min-w-0 flex-1 text-left">{t(sc.labelKey)}</span>
            <span className="shrink-0 text-muted">{sc.getHint(today)}</span>
          </button>
        ))}
      </div>

      <div className="my-appkit-xs border-t border-glass-stroke-subtle" />

      {/* Calendar header */}
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="truncate text-appkit-small font-medium text-primary">
          {monthLabel}
        </span>
        <div className="flex items-center gap-0.5">
          {!isCurrentMonth && (
            <button
              type="button"
              onClick={goToToday}
              className="denker-glass-hover flex h-5 w-5 items-center justify-center rounded-control-sm"
              title={t("goToToday")}
              aria-label={t("goToToday")}
              data-testid="date-picker-today-btn"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            </button>
          )}
          <button
            type="button"
            onClick={goToPrevMonth}
            className="denker-glass-hover flex h-5 w-5 items-center justify-center rounded-control-sm text-muted hover:text-primary"
            aria-label={t("previousMonth")}
            data-testid="date-picker-prev-month"
          >
            <Icons.ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={goToNextMonth}
            className="denker-glass-hover flex h-5 w-5 items-center justify-center rounded-control-sm text-muted hover:text-primary"
            aria-label={t("nextMonth")}
            data-testid="date-picker-next-month"
          >
            <Icons.ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="mb-0.5 grid grid-cols-7 gap-0">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="py-0.5 text-center text-appkit-mini font-medium text-muted"
          >
            {weekdayName(day, "narrow")}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-0">
        {weeks.map((week, wi) =>
          week.map((day, di) => {
            const isToday = isSameDay(day.date, today);
            const isSelected = selectedDate
              ? isSameDay(day.date, selectedDate)
              : false;
            const past = isPast(day.date, today);
            return (
              <button
                key={`${wi}-${di}`}
                type="button"
                onClick={() => selectDate(day.date)}
                className={cn(
                  "flex h-7 w-full items-center justify-center rounded-control-sm text-appkit-small transition-colors",
                  day.inMonth &&
                    !isToday &&
                    !isSelected &&
                    !past &&
                    "denker-glass-hover text-secondary",
                  day.inMonth &&
                    past &&
                    !isToday &&
                    !isSelected &&
                    "text-muted",
                  isToday && !isSelected && "font-semibold text-accent",
                  isSelected && "bg-accent font-semibold text-black",
                )}
                data-testid={`date-picker-day-${fmtDate(day.date)}`}
              >
                {day.date.getDate()}
              </button>
            );
          }),
        )}
      </div>

      <div className="my-appkit-xs border-t border-glass-stroke-subtle" />

      {/* Time pill */}
      <button
        type="button"
        onClick={() => setView("time")}
        className="denker-glass-hover flex w-full items-center justify-center gap-appkit-xs rounded-control border border-glass-stroke-subtle py-1.5 text-appkit-small text-secondary"
        data-testid="date-picker-time-toggle"
      >
        <Icons.Clock className="h-3.5 w-3.5 text-muted" />
        {timeValue ? (
          <span className="text-accent">{timeValue}</span>
        ) : (
          <span>{t("time")}</span>
        )}
      </button>

      {/* Repeat pill */}
      <button
        type="button"
        onClick={() => setView("repeat")}
        className="denker-glass-hover mt-1 flex w-full items-center justify-center gap-appkit-xs rounded-control border border-glass-stroke-subtle py-1.5 text-appkit-small text-secondary"
        data-testid="date-picker-repeat-toggle"
      >
        <Icons.Repeat2 className="h-3.5 w-3.5 text-muted" />
        <span className={cn("min-w-0 truncate", repeat && "text-accent")}>
          {repeat ? repeatLabel(repeat) : t("repeat")}
        </span>
      </button>

      {/* Clear */}
      {value && (
        <>
          <div className="my-appkit-xs border-t border-glass-stroke-subtle" />
          <button
            type="button"
            onClick={onClear}
            className={CLEAR_BUTTON_CLASS}
            data-testid="date-picker-clear"
          >
            {t("clearDate")}
          </button>
        </>
      )}
    </DatePickerSurface>
  );
}

function renderIcon(name: keyof typeof Icons) {
  const Icon = Icons[name];
  return <Icon className="h-3.5 w-3.5 text-muted" />;
}
